// #31: guard the whole module so re-executing this section script (theme-editor
// reload / Quick Add re-injection) does not re-declare its top-level bindings or
// re-register the custom element ("already declared"/"already defined").
if (!customElements.get('testimonials-slider')) {
// Constants
const BREAKPOINTS = {
  MOBILE_MAX: 749,
  TABLET_MIN: 750,
  DESKTOP_MIN: 1024,
};

const TIMEOUTS = {
  USER_NAV_LOCK: 500,
  SWIPER_SPEED: 500,
  ANIMATION_BUFFER: 50,
  PLAYBACK_RETRY: 250,
  PLAYBACK_FALLBACK: 300,
  ANIMATION_CHECK: 120,
  VIDEO_LOAD_TIMEOUT: 10000, // 10 seconds max to start playing
};

const PREVIEW = {
  MIN_DURATION: 4,
  DEFAULT_DURATION: 4,
  DEFAULT_START: 0,
};

const OBSERVER = {
  THRESHOLD: 0.3,
};

class TestimonialsSlider extends HTMLElement {
  constructor() {
    super();

    this.settings = this.getSettings();
    this.customerCount = this.settings.customer_count;
    this.googleCount = this.settings.google_count;
    this.googleStars = this.settings.google_stars;
    this.trustedshopsCount = this.settings.trustedshops_count;
    this.trustedshopsStars = this.settings.trustedshops_stars;

    this.swiper = null;
    this.sliderEl = null;

    this.currentIframe = null;
    this.previewTimer = null;
    this.previewLoopActive = false;

    this.isFullMode = false;

    this.centeredActivated = false;

    this.inViewport = false;
    this.viewportObserver = null;

    this.previewIndex = 0;
    this.isAutoSliding = false;

    this.previewCycleId = 0;
    this.userNavLockTimer = null;

    // Cache for event listeners cleanup
    this.boundEventListeners = new WeakMap();

    this.replaceReviewPlaceholders();
  }

  connectedCallback() {
    this.initSwiper();
    this.initVideoPosters();
    this.initViewportObserver();
    this.startAutoPreviewLoop();
  }

  disconnectedCallback() {
    this.stopAutoPreviewLoop();
    this.pauseAllVideos();

    // Cleanup viewport observer
    if (this.viewportObserver) {
      this.viewportObserver.disconnect();
      this.viewportObserver = null;
    }

    // Cleanup video event listeners
    const videos = this.querySelectorAll('.testimonials-slider--shopify-video');
    videos.forEach(video => {
      const listeners = this.boundEventListeners.get(video);
      if (listeners) {
        video.removeEventListener('click', listeners.togglePlayPause);
        video.removeEventListener('touchstart', listeners.togglePlayPause);
        video.removeEventListener('play', listeners.onPlay);
        video.removeEventListener('pause', listeners.onPause);
        video.removeEventListener('ended', listeners.onEnded);
      }
    });

    // Cleanup play button event listeners
    const playButtons = this.querySelectorAll('.deferred-media__poster-button');
    playButtons.forEach(button => {
      const listeners = this.boundEventListeners.get(button);
      if (listeners && listeners.click) {
        button.removeEventListener('click', listeners.click);
      }
    });

    if (this.swiper && !this.swiper.destroyed) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.sliderEl = null;
  }

  replaceReviewPlaceholders() {
    const titleWrapper = this.querySelector('.testimonials-slider--title');
    if (titleWrapper) {
      const heading = titleWrapper.querySelector('h2');
      if (heading && heading.textContent.includes('{customer_count}')) {
        heading.textContent = heading.textContent.replace('{customer_count}', this.customerCount);
      }
    }

    const bottomContainer = this.querySelector('.custom-reviews--bottom');
    if (bottomContainer) {
      const paragraphs = bottomContainer.querySelectorAll('p');

      paragraphs.forEach(p => {
        let html = p.innerHTML;

        if (html.includes('{google_stars}') && this.googleStars) {
          html = html.replace('{google_stars}', `<strong>${this.googleStars}</strong>`);
        }

        if (html.includes('{google_count}') && this.googleCount) {
          html = html.replace('{google_count}', this.googleCount);
        }

        if (html.includes('{trustedshops_stars}') && this.trustedshopsStars) {
          html = html.replace('{trustedshops_stars}', `<strong>${this.trustedshopsStars}</strong>`);
        }

        if (html.includes('{trustedshops_count}') && this.trustedshopsCount) {
          html = html.replace('{trustedshops_count}', this.trustedshopsCount);
        }

        p.innerHTML = html;
      });

      const itemText = bottomContainer.querySelectorAll('.custom-reviews--bottom-item--text');
      itemText.forEach(item => {
        item.classList.remove('is-hidden');
      });
    }
  }

  getSettings() {
    const configEl = document.getElementById('reviews-config');
    if (!configEl) {
      console.warn('reviews-config element not found');
      return {};
    }

    try {
      const content = configEl.innerHTML.trim();
      return content ? JSON.parse(content) : {};
    } catch (error) {
      console.error('Failed to parse reviews-config:', error);
      return {};
    }
  }

  /* ====== SWIPER ====== */

  initSwiper() {
    if (this.swiper) return;

    this.sliderEl = this.querySelector('.testimonials-slider--gradient.swiper');
    if (!this.sliderEl) return;

    if (typeof Swiper === 'undefined') {
      console.warn('Swiper is not loaded for TestimonialsSlider');
      return;
    }

    this.swiper = new Swiper(this.sliderEl, {
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      spaceBetween: 12,
      speed: TIMEOUTS.SWIPER_SPEED,
      centeredSlides: true,
      centeredSlidesBounds: true,
      loop: false,

      // swipe mobile/tablet
      grabCursor: window.innerWidth < BREAKPOINTS.DESKTOP_MIN,
      allowTouchMove: window.innerWidth < BREAKPOINTS.DESKTOP_MIN,

      breakpoints: {
        // TABLET — 2 full slides, no centering
        [BREAKPOINTS.TABLET_MIN]: {
          slidesPerView: 2,
          slidesPerGroup: 2,
          spaceBetween: 20,
          centeredSlides: false,
          centeredSlidesBounds: false,
          allowTouchMove: true,
          grabCursor: true,
        },

        // DESKTOP — 3 full slides, no centering, swipe disabled
        [BREAKPOINTS.DESKTOP_MIN]: {
          slidesPerView: 3,
          slidesPerGroup: 3,
          spaceBetween: 23,
          centeredSlides: false,
          centeredSlidesBounds: false,
          allowTouchMove: false,
          grabCursor: false,
        },
      },

      navigation: {
        nextEl: this.querySelector('.testimonials-slider--nav-next'),
        prevEl: this.querySelector('.testimonials-slider--nav-prev'),
      },

      on: {
        init: swiper => {
          this.handleNavigationVisibility(swiper);
          this.updateNavButtonPosition();

          // Set initial index without sliding
          this.previewIndex = swiper.activeIndex || 0;
        },

        slideChange: swiper => {
          this.handleNavigationVisibility(swiper);
          this.updateNavButtonPosition();

          if (!this.isAutoSliding) {
            // User-initiated navigation (arrows or swipe) — treat uniformly
            this.previewIndex = swiper.activeIndex;
            this.onUserNavigation();
          }

          if (this.isFullMode) {
            this.isFullMode = false;
            this.pauseAllVideos();
          }
        },

        slideChangeTransitionEnd: swiper => {
          this.isAutoSliding = false;
        },

        resize: swiper => {
          const touch = window.innerWidth < BREAKPOINTS.DESKTOP_MIN;
          swiper.allowTouchMove = touch;
          swiper.params.allowTouchMove = touch;
          swiper.params.grabCursor = touch;
          swiper.update();

          this.handleNavigationVisibility(swiper);
          this.updateNavButtonPosition();
        },
      },
    });

  }

  handleNavigationVisibility(swiper) {
    const prevBtn = this.querySelector('.testimonials-slider--nav-prev');
    const nextBtn = this.querySelector('.testimonials-slider--nav-next');

    if (!prevBtn || !nextBtn) return;

    if (swiper.isBeginning) {
      prevBtn.classList.add('is-hidden');
    } else {
      prevBtn.classList.remove('is-hidden');
    }

    if (swiper.isEnd) {
      nextBtn.classList.add('is-hidden');
    } else {
      nextBtn.classList.remove('is-hidden');
    }
  }

  updateNavButtonPosition() {
    if (!this.swiper) return;

    const prevArrow = this.querySelector('.testimonials-slider--nav-prev');
    const nextArrow = this.querySelector('.testimonials-slider--nav-next');

    if (!prevArrow || !nextArrow) return;

    const screenWidth = window.innerWidth;
    const isFirstSlide = this.swiper.isBeginning;
    const isLastSlide = this.swiper.isEnd;

    const allClasses = ['isFirstSlide', 'isLastSlide', 'otherSlideMobile', 'otherSlideDesktop'];
    prevArrow.classList.remove(...allClasses);
    nextArrow.classList.remove(...allClasses);

    if (screenWidth <= BREAKPOINTS.MOBILE_MAX) {
      if (isFirstSlide) {
        prevArrow.classList.add('isFirstSlide');
        nextArrow.classList.add('isFirstSlide');
      } else if (isLastSlide) {
        prevArrow.classList.add('isLastSlide');
        nextArrow.classList.add('isLastSlide');
      } else {
        prevArrow.classList.add('otherSlideMobile');
        nextArrow.classList.add('otherSlideMobile');
      }
    } else {
      prevArrow.classList.add('otherSlideDesktop');
      nextArrow.classList.add('otherSlideDesktop');
    }
  }

  /* ====== VIDEO + POSTER + OVERLAY ====== */

  initVideoPosters() {
    const slides = this.querySelectorAll('.testimonials-slider--item--video');

    slides.forEach(slide => {
      const video = slide.querySelector('.testimonials-slider--shopify-video');
      const posterBtn = slide.querySelector('.testimonials-slider__poster');
      const videoWrapper = slide.querySelector('.testimonials-slider--video');

      if (!video || !posterBtn || !videoWrapper) return;

      // Ensure poster is set (should already be in HTML now, but double-check)
      const posterUrl = video.dataset.poster;
      if (posterUrl && !video.hasAttribute('poster')) {
        video.poster = posterUrl;
        video.setAttribute('poster', posterUrl);
      }

      const showUI = () => {
        videoWrapper.classList.remove('is-playing', 'is-preview');
        videoWrapper.classList.add('is-stopped');
      };

      const hideUI = () => {
        videoWrapper.classList.remove('is-stopped', 'is-preview');
        videoWrapper.classList.add('is-playing');
      };

      const startFullPlayback = () => {
        this.isFullMode = true;
        this.stopAutoPreviewLoop();
        this.pauseAllVideos(video);

        this.ensureVideoSources(video);

        video.controls = false;
        video.removeAttribute('controls');

        video.removeAttribute('muted');
        video.muted = false;
        video.defaultMuted = false;
        try {
          video.volume = 1;
        } catch (error) {
          console.warn('Failed to set video volume:', error);
        }

        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.preload = 'auto';

        if (!video.dataset.fullStarted) {
          try {
            video.currentTime = 0;
          } catch (error) {
            console.warn('Failed to set video currentTime:', error);
          }
          video.dataset.fullStarted = '1';
        }

        hideUI();

        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      };

      const togglePlayPause = e => {
        if (!this.isFullMode) return;
        e.preventDefault();
        e.stopPropagation();

        if (video.paused) {
          hideUI();
          const p = video.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        } else {
          video.pause();
        }
      };

      const onPlay = () => {
        if (!this.isFullMode) return;
        hideUI();
        // Remove is-stopped class when video plays again
        videoWrapper.classList.remove('is-stopped');
      };

      const onPause = () => {
        if (!this.isFullMode) return;
        showUI();
        // Add is-stopped class when user pauses in full mode
        videoWrapper.classList.add('is-stopped');
      };

      const onEnded = () => {
        showUI();

        if (this.isFullMode) {
          this.isFullMode = false;
          this.startAutoPreviewLoop();
        }
      };

      const onPosterClick = e => {
        e.preventDefault();
        e.stopPropagation();
        startFullPlayback();
      };

      // Start full video - listen on the play button inside poster
      const playButton = posterBtn.querySelector('.deferred-media__poster-button');
      if (playButton) {
        playButton.addEventListener('click', onPosterClick);
      }

      // Pause/Play (full-mode)
      video.addEventListener('click', togglePlayPause);
      video.addEventListener('touchstart', togglePlayPause, { passive: false });
      video.addEventListener('play', onPlay);
      video.addEventListener('pause', onPause);
      video.addEventListener('ended', onEnded);

      // Store listeners for cleanup
      this.boundEventListeners.set(video, {
        togglePlayPause,
        onPlay,
        onPause,
        onEnded,
      });

      if (playButton) {
        this.boundEventListeners.set(playButton, {
          click: onPosterClick,
        });
      }
    });
  }

  pauseAllVideos(exceptVideo) {
    const videos = this.querySelectorAll('.testimonials-slider--shopify-video');

    videos.forEach(videoEl => {
      if (videoEl === exceptVideo) return;

      // Only pause if actually playing
      if (!videoEl.paused) {
        try {
          videoEl.pause();
        } catch (e) {
          // Ignore errors
        }
      }

      const wrap = videoEl.closest('.testimonials-slider--video');

      videoEl.controls = false;
      videoEl.removeAttribute('controls');

      if (wrap) {
        wrap.classList.remove('is-playing', 'is-preview', 'is-stopped');
      }
    });
  }

  /* ====== AUTOPREVIEW: 4-SECOND LOOP ====== */

  startAutoPreviewLoop() {
    if (!this.swiper) return;
    if (this.isFullMode) return;
    if (!this.inViewport) return;

    this.stopAutoPreviewLoop();
    this.previewLoopActive = true;
    this.runPreviewForActiveSlide();
  }

  stopAutoPreviewLoop() {
    this.previewLoopActive = false;
    this.previewCycleId++;

    if (this.previewTimer) {
      clearTimeout(this.previewTimer);
      this.previewTimer = null;
    }

    if (this.userNavLockTimer) {
      clearTimeout(this.userNavLockTimer);
      this.userNavLockTimer = null;
    }
  }

  onUserNavigation() {
    this.stopAutoPreviewLoop();
    this.pauseAllVideos();

    this.userNavLockTimer = setTimeout(() => {
      if (!this.isFullMode && this.inViewport && document.visibilityState === 'visible') {
        this.startAutoPreviewLoop();
      }
    }, TIMEOUTS.USER_NAV_LOCK);
  }

  runPreviewForActiveSlide() {
    if (!this.previewLoopActive || !this.swiper) return;
    if (!this.inViewport) return;
    if (this.isFullMode) return;
    const cycleId = this.previewCycleId;

    const swiper = this.swiper;
    const totalSlides = swiper.slides.length;

    if (typeof this.previewIndex !== 'number' || this.previewIndex < 0 || this.previewIndex >= totalSlides) {
      this.previewIndex = swiper.activeIndex || 0;
    }

    const activeSlide = swiper.slides[this.previewIndex];
    if (!activeSlide) {
      this.scheduleNextPreview();
      return;
    }

    const video = activeSlide.querySelector('.testimonials-slider--shopify-video');
    const videoWrapper = activeSlide.querySelector('.testimonials-slider--video');

    if (!video || !videoWrapper) {
      this.scheduleNextPreview();
      return;
    }

    const startTime = parseFloat(activeSlide.dataset.previewStart || PREVIEW.DEFAULT_START) || PREVIEW.DEFAULT_START;

    // Stop other videos more carefully
    const videos = this.querySelectorAll('.testimonials-slider--shopify-video');
    videos.forEach(v => {
      if (v !== video && !v.paused) {
        v.pause();
      }
    });

    // Ensure video sources are loaded FIRST
    this.ensureVideoSources(video);

    // Preload next video early
    this.preloadNextVideo();

    const durationSecRaw = parseFloat(activeSlide.dataset.previewDuration || PREVIEW.DEFAULT_DURATION);
    const durationSec = Number.isFinite(durationSecRaw) ? Math.max(PREVIEW.MIN_DURATION, durationSecRaw) : PREVIEW.DEFAULT_DURATION;
    const durationMs = Math.round(durationSec * 1000);

    let playbackStarted = false;
    let cleanupDone = false;

    const cleanup = () => {
      if (cleanupDone) return;
      cleanupDone = true;

      if (this.previewTimer) {
        clearTimeout(this.previewTimer);
        this.previewTimer = null;
      }
      videoWrapper.classList.remove('is-preview');
    };

    const moveToNext = () => {
      if (cycleId !== this.previewCycleId) return;
      cleanup();
      if (!video.paused) video.pause();
      this.scheduleNextPreview();
    };

    // Setup video for preview
    video.controls = false;
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.preload = 'metadata';

    try {
      video.currentTime = startTime;
    } catch (e) {
      // Ignore - video may not be ready yet
    }

    // Wait for video to actually render first frame
    const onTimeUpdate = () => {
      if (cycleId !== this.previewCycleId) return;
      if (playbackStarted) return;

      // Only proceed if video has progressed beyond start time
      if (video.currentTime > startTime) {
        playbackStarted = true;
        videoWrapper.classList.add('is-preview');

        // Remove the timeupdate listener
        video.removeEventListener('timeupdate', onTimeUpdate);

        // Start preview duration timer
        this.previewTimer = setTimeout(() => {
          if (cycleId !== this.previewCycleId) return;
          moveToNext();
        }, durationMs);
      }
    };

    // Simple warning after 10 seconds if video hasn't started
    setTimeout(() => {
      if (!playbackStarted && cycleId === this.previewCycleId) {
        console.warn('Video is taking longer than 10 seconds to load:', video.dataset.srcMp4 || video.dataset.srcWebm);
      }
    }, TIMEOUTS.VIDEO_LOAD_TIMEOUT);

    video.addEventListener('timeupdate', onTimeUpdate);

    // Try to play
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        video.removeEventListener('timeupdate', onTimeUpdate);
        if (cycleId === this.previewCycleId) {
          console.warn('Video play promise rejected:', video.dataset.srcMp4 || video.dataset.srcWebm);
          moveToNext();
        }
      });
    }
  }

  scheduleNextPreview() {
    const cycleId = this.previewCycleId;
    if (!this.previewLoopActive || !this.swiper) return;
    if (this.isFullMode) return;

    const swiper = this.swiper;
    // If Swiper is animating (user just clicked), don't interfere
    if (swiper.animating) {
      this.previewTimer = setTimeout(() => {
        if (cycleId !== this.previewCycleId) return;
        this.scheduleNextPreview();
      }, TIMEOUTS.ANIMATION_CHECK);
      return;
    }
    const speed = swiper.params.speed || TIMEOUTS.SWIPER_SPEED;
    const totalSlides = swiper.slides.length;

    if (!totalSlides) return;

    // current group size (mobile = 1, tablet = 2, desktop = 3)
    const groupSize = Number(swiper.params.slidesPerGroup) || 1;

    // safety check
    if (typeof this.previewIndex !== 'number' || this.previewIndex < 0 || this.previewIndex >= totalSlides) {
      this.previewIndex = swiper.activeIndex || 0;
    }

    // ===== MOBILE (groupSize = 1): same behavior as before — slide every time
    if (groupSize === 1) {
      this.previewIndex = (this.previewIndex + 1) % totalSlides;

      this.isAutoSliding = true;
      swiper.slideTo(this.previewIndex);

      this.previewTimer = setTimeout(() => {
        if (cycleId !== this.previewCycleId) return;
        this.runPreviewForActiveSlide();
      }, speed + TIMEOUTS.ANIMATION_BUFFER);

      return;
    }

    // ===== TABLET / DESKTOP (groupSize = 2 / 3):
    // inside one group: 0 → 1 → 2 (no swiper slide movement),
    // then jump to the next group (with swiper slide transition)

    const groupStart = Math.floor(this.previewIndex / groupSize) * groupSize;
    const groupEndExclusive = Math.min(groupStart + groupSize, totalSlides);

    const nextInGroup = this.previewIndex + 1;

    // 1) next item inside the group — do NOT move Swiper, only update previewIndex
    if (nextInGroup < groupEndExclusive) {
      this.previewIndex = nextInGroup;

      this.previewTimer = setTimeout(() => {
        if (cycleId !== this.previewCycleId) return;
        this.runPreviewForActiveSlide();
      }, TIMEOUTS.ANIMATION_BUFFER);

      return;
    }

    // 2) group finished — slide Swiper to the next group
    let nextGroupStart = groupStart + groupSize;
    if (nextGroupStart >= totalSlides) nextGroupStart = 0;

    this.previewIndex = nextGroupStart;

    this.isAutoSliding = true;
    swiper.slideTo(nextGroupStart);

    this.previewTimer = setTimeout(() => {
      if (cycleId !== this.previewCycleId) return;
      this.runPreviewForActiveSlide();
    }, speed + TIMEOUTS.ANIMATION_BUFFER);
  }

  initViewportObserver() {
    if (!('IntersectionObserver' in window)) {
      this.inViewport = true;
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const nowVisible = entry.isIntersecting;

          if (nowVisible && !this.inViewport) {
            this.inViewport = true;
            if (!this.isFullMode) {
              this.startAutoPreviewLoop();
            }
          }

          if (!nowVisible && this.inViewport) {
            this.inViewport = false;
            this.stopAutoPreviewLoop();
            this.pauseAllVideos();
          }
        });
      },
      { threshold: OBSERVER.THRESHOLD }
    );

    observer.observe(this);
    this.viewportObserver = observer;
  }

  isSafari() {
    const ua = navigator.userAgent;
    const vendor = navigator.vendor || '';
    const isApple = /Apple/.test(vendor);
    const isSafari = isApple && /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);

    // Additional check for iOS devices
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;

    // Safari on macOS or iOS
    return isSafari || isIOS;
  }

  preloadNextVideo() {
    if (!this.swiper) return;

    const totalSlides = this.swiper.slides.length;
    if (totalSlides <= 1) return;

    // Calculate next index
    let nextIndex = this.previewIndex + 1;
    if (nextIndex >= totalSlides) nextIndex = 0;

    const nextSlide = this.swiper.slides[nextIndex];
    if (!nextSlide) return;

    const nextVideo = nextSlide.querySelector('.testimonials-slider--shopify-video');
    if (!nextVideo) return;

    // Preload sources for next video
    this.ensureVideoSources(nextVideo);

    // Set preload hint
    if (nextVideo.preload !== 'auto') {
      nextVideo.preload = 'metadata';
    }
  }

  ensureVideoSources(video) {
    if (!video) return;

    // Check if sources are already loaded - CRITICAL to prevent duplicates
    if (video.dataset.sourcesLoaded === '1') {
      return;
    }

    const mp4 = video.dataset.srcMp4;
    const webm = video.dataset.srcWebm;

    if (!mp4 && !webm) {
      console.warn('No video sources available for slide');
      return;
    }

    // Mark IMMEDIATELY to prevent race conditions
    video.dataset.sourcesLoaded = '1';

    const safari = this.isSafari();

    // Safari doesn't support WebM format at all (no VP8/VP9 codec support)
    // We must use MP4 (H.264) for Safari on iOS and macOS
    if (safari) {
      if (mp4) {
        const source = document.createElement('source');
        source.src = mp4;
        source.type = 'video/mp4';
        video.appendChild(source);
      } else {
        console.warn('No MP4 source available for Safari');
      }
    } else {
      // Other browsers: prefer WebM (better compression), fallback to MP4
      if (webm) {
        const source = document.createElement('source');
        source.src = webm;
        source.type = 'video/webm';
        video.appendChild(source);
      }

      if (mp4) {
        const source = document.createElement('source');
        source.src = mp4;
        source.type = 'video/mp4';
        video.appendChild(source);
      }
    }

    // Load video metadata (triggers network request)
    video.load();
  }
}

customElements.define('testimonials-slider', TestimonialsSlider);
}
